/**
 * Modality Utilities
 * Handles fetching and retrieving modalities from API and localStorage
 */

import { API_ENDPOINTS, STORAGE_KEYS } from '../config/api.config';
import { TEMPLATE_MODALITY_OPTIONS } from '../constants';

export interface Modality {
  id: number;
  name: string;
  description: string;
}

export interface ModalityOption {
  id: number;
  value: string;
  label: string;
}

/** UI labels for known RIS modality codes (matches Templates / Imaging worklist). */
const MODALITY_DISPLAY_LABELS: Record<string, string> = {
  MR: 'MRI',
  CT: 'CT',
  XR: 'X-Ray',
  US: 'USG',
};

/**
 * Friendly label for a modality code from the API (e.g. MR → MRI, XR → X-Ray, US → USG).
 */
export function formatModalityDisplayLabel(modalityName: string): string {
  const key = modalityName.trim().toUpperCase();
  return MODALITY_DISPLAY_LABELS[key] ?? modalityName;
}

/** Map `/ris/api/modalities` rows to select/pill options (`value` = API code, `label` = UI). */
export function mapApiModalitiesToOptions(modalities: Modality[]): ModalityOption[] {
  return modalities.map(modality => ({
    id: modality.id,
    value: modality.name,
    label: formatModalityDisplayLabel(modality.name),
  }));
}

const ROUTING_RULE_MODALITY_ALIASES: Record<string, string[]> = {
  MR: ['MR', 'MRI'],
  CT: ['CT'],
  XR: ['XR'],
  US: ['US', 'USG'],
};

/**
 * Routing rules only expose the four core imaging modalities (MRI, CT, X-Ray, USG).
 * Resolves API rows when present; falls back to canonical codes from templates/worklist.
 */
export function getRoutingRuleModalityOptions(modalities: Modality[] | null): ModalityOption[] {
  const apiList = modalities ?? [];

  return TEMPLATE_MODALITY_OPTIONS.map((option, index) => {
    const aliases = ROUTING_RULE_MODALITY_ALIASES[option.value] ?? [option.value];
    const match = apiList.find(item =>
      aliases.some(alias => alias.toUpperCase() === item.name.trim().toUpperCase())
    );

    return {
      id: match?.id ?? index + 1,
      value: match?.name ?? option.value,
      label: option.label,
    };
  });
}

function normalizeModalityCode(value: string): string {
  return value.trim().toUpperCase();
}

/** Whether two modality codes refer to the same modality (MR/MRI, XR/X-Ray, US/USG). */
export function modalityCodesMatch(selected: string, candidate: string): boolean {
  const left = normalizeModalityCode(selected);
  const right = normalizeModalityCode(candidate);
  if (left === right) {
    return true;
  }
  if ((left === 'MR' || left === 'MRI') && (right === 'MR' || right === 'MRI')) {
    return true;
  }
  if ((left === 'XR' || left === 'XRAY') && (right === 'XR' || right === 'XRAY')) {
    return true;
  }
  if ((left === 'US' || left === 'USG') && (right === 'US' || right === 'USG')) {
    return true;
  }
  return false;
}

/**
 * Map DICOM modality to backend modality format
 * Backend modalities: CT, MR, XR, US, USG, Fluoroscopy, Mammography, Nuclear Medicine, PET, Other
 */
export function mapDicomModalityToBackend(dicomModality: string): string | null {
  if (!dicomModality) {
    return null;
  }

  const modality = dicomModality.toUpperCase().trim();

  // Direct mappings
  if (modality === 'CT') {
    return 'CT';
  }
  if (modality === 'MR' || modality === 'MRI') {
    return 'MR';
  }
  if (modality === 'XR') {
    return 'XR';
  }
  if (modality === 'US') {
    return 'US';
  }
  if (modality === 'USG') {
    return 'USG';
  }
  if (modality === 'PET') {
    return 'PET';
  }

  // X-Ray family: CR, DX -> XR
  if (modality === 'CR' || modality === 'DX') {
    return 'XR';
  }

  // Mammography
  if (modality === 'MG' || modality === 'MAMMOGRAPHY') {
    return 'Mammography';
  }

  // Nuclear Medicine
  if (modality === 'NM' || modality === 'NUCLEAR MEDICINE') {
    return 'Nuclear Medicine';
  }

  // Fluoroscopy
  if (modality === 'RF' || modality === 'FLUOROSCOPY') {
    return 'Fluoroscopy';
  }

  // Default to Other for unknown modalities
  return 'Other';
}

/**
 * Get modality ID from stored modalities by matching modality name
 */
export function getModalityId(modalityName: string): number | null {
  try {
    const modalities = getModalities();
    if (!modalities || !Array.isArray(modalities)) {
      return null;
    }

    // First try exact match
    const exactMatch = modalities.find(m => m.name === modalityName);
    if (exactMatch) {
      return exactMatch.id;
    }

    // Try case-insensitive match
    const caseInsensitiveMatch = modalities.find(
      m => m.name.toLowerCase() === modalityName.toLowerCase()
    );
    if (caseInsensitiveMatch) {
      return caseInsensitiveMatch.id;
    }

    // Try mapping DICOM modality to backend format and then find ID
    const backendModality = mapDicomModalityToBackend(modalityName);
    if (backendModality) {
      const backendMatch = modalities.find(m => m.name === backendModality);
      if (backendMatch) {
        return backendMatch.id;
      }
    }
  } catch (error) {
    // Error getting modality ID
  }
  return null;
}

/**
 * Fetch modalities from API and store in localStorage
 */
export async function fetchAndStoreModalities(): Promise<void> {
  try {
    const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(API_ENDPOINTS.MODALITIES.GET, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch modalities: ${response.statusText}`);
    }

    const responseData = await response.json();

    if (responseData.success && Array.isArray(responseData.data)) {
      // Store modalities in localStorage
      localStorage.setItem(STORAGE_KEYS.MODALITIES, JSON.stringify(responseData.data));
    } else {
      throw new Error('Invalid modalities response format');
    }
  } catch (error) {
    console.error('Error fetching modalities:', error);
    throw error;
  }
}

/**
 * Get modalities from localStorage
 */
export function getModalities(): Modality[] | null {
  try {
    const modalitiesStr = localStorage.getItem(STORAGE_KEYS.MODALITIES);
    if (!modalitiesStr) {
      return null;
    }
    return JSON.parse(modalitiesStr);
  } catch (error) {
    console.error('Error parsing modalities from storage:', error);
    return null;
  }
}
